import type { Question } from "@/schema/quizSchema";

type NumberQuestion = Extract<Question, { type: "number" }>;

/**
 * Parse a raw <input type="number"> value into the stored answer.
 * Empty string → null (cleared). Non-finite parses (Number("1.2.3"),
 * Number("-") => NaN) → null, never a stored NaN — a NaN would silently skew
 * scoring/classifier comparisons (NaN >= 7 is false) and bypass numeric-default
 * fallbacks (typeof NaN === "number"). Exported so the guard is unit-testable:
 * a real <input type="number"> sanitizes invalid strings to "" in jsdom, which
 * would otherwise make the NaN branch unreachable from a DOM-level test.
 */
export function parseNumberInput(raw: string): number | null {
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function NumberInput({
  question,
  value,
  onAnswer,
}: {
  question: NumberQuestion;
  value: number | null | undefined;
  onAnswer: (value: number | null) => void;
}) {
  return (
    <div>
      <label className="qk-prompt">
        {question.prompt}
        {question.unit ? ` (${question.unit})` : ""}
      </label>
      <input
        type="number"
        className="qk-field"
        aria-label={question.prompt}
        placeholder={question.placeholder}
        min={question.min}
        max={question.max}
        step={question.step ?? 1}
        value={value ?? ""}
        onChange={(e) => onAnswer(parseNumberInput(e.target.value))}
      />
    </div>
  );
}
